import React                   from 'react';
import {
  IAutoCompleteRenderSuggestionFnProps,
  IFormContext,
  withStyles,
  ISuggestionsContainerComponentProps,
  IFieldDefn,
}                              from 'rewire-ui';
import Button, { ButtonProps } from '@material-ui/core/Button';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText            from '@material-ui/core/ListItemText';
import { Theme }               from '@material-ui/core/styles';
import { SvgIconProps }        from '@material-ui/core/SvgIcon';
import Typography              from '@material-ui/core/Typography';
import EditIcon                from '@material-ui/icons/EditOutlined';

export type AdvancedAutoCompleteActionType           = {action: () => (Promise<void> | void), label: string, icon?: React.ComponentType<SvgIconProps>, color?: 'primary' | 'secondary', variant?: ButtonProps['variant'], disabled?: () => boolean};
export type AdvancedAutoCompleteSuggestionActionType = {action: (id: string) => (Promise<void> | void), label?: string, icon?: React.ComponentType<SvgIconProps>, color?: 'primary' | 'secondary', variant?: ButtonProps['variant'], disabled?: () => boolean};

const suggestionsContainerHeader = (title: string | JSX.Element): React.SFC<ISuggestionsContainerComponentProps> => React.memo(() => (
  typeof title === 'string'
    ? <div>
        <Typography variant='subtitle1' style={{fontSize: 'inherit'}}><strong>{title}</strong></Typography>
      </div>
    : title
));

const suggestionsContainerFooterStyles = (_theme: Theme) => ({
  button: {
    fontSize: 'inherit',
    padding: '0.5em 1em',
    boxShadow: 'none',
    textTransform: 'none',
  },
  actionIcon: {
    marginRight: '0.5em',
  },
});

const suggestionsContainerFooter = (action: AdvancedAutoCompleteActionType): React.SFC<ISuggestionsContainerComponentProps> => React.memo(withStyles(suggestionsContainerFooterStyles, (props: any) => {
  const {classes} = props;
  return (
    <div>
      <Button className={classes.button} color={action.color} variant={action.variant || 'contained'} disabled={action.disabled?.()} onClick={action.action}>
        {action.icon && <action.icon className={action.label ? classes.actionIcon : undefined} />}
        {action.label && <span>{action.label}</span>}
      </Button>
    </div>
  );
}));

const getSuggestionActionClickHandler = (id: string, action: (id: string) => (Promise<void> | void)) => (evt: React.MouseEvent<any>) => {
  action(id);
  evt.stopPropagation();
};

const suggestionRendererStyles = (_theme: Theme) => ({
  text: {
    paddingRight: '36px',
  },
  secondaryAction: {
    top: '0px',
    right: '0px',
    transform: 'none',
    display: 'flex',
    height: '100%',
  },
  secondaryActionButton: {
    minWidth: '0px',
    padding: '0px 8px 0px 12px',
    borderRadius: '0px',
  },
  actionIcon: {
    marginRight: '0.5em',
  },
  highlighted: {
    fontWeight: 700,
  },
  notHighlighted: {
    fontWeight: 300,
  },
});

type AutoCompleteSuggestionType = IAutoCompleteRenderSuggestionFnProps<object | string>;
export const getAdvancedAutoCompleteSuggestionRenderer = (suggestionAction?: AdvancedAutoCompleteSuggestionActionType): React.SFC<AutoCompleteSuggestionType> => React.memo(withStyles(suggestionRendererStyles, (props: any) => {
  const {classes} = props;
  return (
    < >
    <ListItemText className={classes.text}>
      {props.parts.map((part: any, index: number) => {
        return (
          <span key={String(index)} className={part.highlight ? classes.highlighted : classes.notHighlighted} dangerouslySetInnerHTML={{__html: part.text}} />
        );
      })}
    </ListItemText>
    {suggestionAction &&
      <ListItemSecondaryAction className={classes.secondaryAction}>
        <Button color={suggestionAction.color} variant={suggestionAction.variant} className={classes.secondaryActionButton} disabled={suggestionAction.disabled?.()} onClick={getSuggestionActionClickHandler(props.suggestion.id, suggestionAction.action)}>
          {suggestionAction.icon ? <suggestionAction.icon className={suggestionAction.label && classes.actionIcon} /> : <EditIcon />}
          {suggestionAction.label && <span>{suggestionAction.label}</span>}
        </Button>
      </ListItemSecondaryAction>
    }
    </>
  );
}));

export const getAdvancedAutoCompleteOptions = (title?: string | JSX.Element, action?: AdvancedAutoCompleteActionType, suggestionAction?: AdvancedAutoCompleteSuggestionActionType, editProps?: any) => {
  return ({
    suggestionsContainerHeader: title && suggestionsContainerHeader(title),
    suggestionsContainerFooter: action && suggestionsContainerFooter(action),
    renderSuggestion:           suggestionAction && getAdvancedAutoCompleteSuggestionRenderer(suggestionAction),
    openOnFocus:                true,
    showEmptySuggestions:       true,
    ...editProps,
  });
};

export const getAdvancedFormAutoComplete = (form: IFormContext, searcher: any, title?: string | JSX.Element, action?: AdvancedAutoCompleteActionType, suggestionAction?: AdvancedAutoCompleteSuggestionActionType, editProps?: any): IFieldDefn => {
  const referenceOptions = getAdvancedAutoCompleteOptions(title, action, suggestionAction, editProps);
  return form.reference(searcher, referenceOptions);
};

export const getAdvancedFormMultiSelectAutoComplete = (form: IFormContext, searcher: any, title?: string | JSX.Element, action?: AdvancedAutoCompleteActionType, suggestionAction?: AdvancedAutoCompleteSuggestionActionType, editProps?: any): IFieldDefn => {
  const referenceOptions = getAdvancedAutoCompleteOptions(title, action, suggestionAction, editProps);
  return form.multiselectautocomplete(searcher, referenceOptions);
};
